
// src/pages/PrepMaterials.jsx
import React, { useState, useEffect } from "react";
import { Upload, Trash2, FileText, Edit, Download, Plus } from "lucide-react";
import { 
  uploadPrepMaterial, 
  getPrepMaterials, 
  deletePrepMaterial, 
  updatePrepMaterial 
} from "../api/prepClient";
import { getMyQuestions, startMockInterview } from "../api/mockClient";
import { useNavigate , useLocation } from "react-router-dom";
import api from "../api/api"; // for adding new question(s)
import Notes from "../components/Notes";

import PdfHighlighterComponent from "../components/PdfHighlighter";
import TextHighlighter from "../components/TextHighlighter";
import Layout from "../components/Layout";   // ⬅️ Add this after other imports
import DownloadButton from "../components/DownloadButton"; // adjust path if needed




export default function PrepMaterials() {
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState("");
  const [usage, setUsage] = useState({ usedBytes: 0, limitBytes: 524288000 });
  const [editing, setEditing] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // mock interview / questions states
  const [questionCount, setQuestionCount] = useState(0);
  const [mockLoading, setMockLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({ questionText: "", domain: "" });
  const [bulkText, setBulkText] = useState("");
  const [questionError, setQuestionError] = useState("");
  const [versions, setVersions] = useState([]);  // ✅ add this

  const navigate = useNavigate();
  const location = useLocation();

  // grab versionId from URL
  const searchParams = new URLSearchParams(location.search);
const versionId = searchParams.get("version");
const mode = searchParams.get("mode"); 
  

  // Fetch materials & questions on mount
  useEffect(() => {
    fetchMaterials();
    fetchQuestions();
      fetchVersions();
  }, []);

  useEffect(() => {
  console.log("Versions loaded:", versions);
}, [versions]);


  // Filter materials when search or type changes
  useEffect(() => {
    let filtered = materials;
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(
        (m) => m.title.toLowerCase().includes(s) || m.notes?.toLowerCase().includes(s)
      );
    }
    if (fileTypeFilter) {
      filtered = filtered.filter((m) =>
        m.mimeType.toLowerCase().includes(fileTypeFilter.toLowerCase())
      );
    }
    setFilteredMaterials(filtered);
  }, [materials, search, fileTypeFilter]);

  const fetchMaterials = async () => {
    const data = await getPrepMaterials();
    setMaterials(data.items || []);
    setUsage(data.usage || { usedBytes: 0, limitBytes: 524288000 });
  };

  const fetchQuestions = async () => {
    try {
      const res = await getMyQuestions();
      const items = Array.isArray(res.data) ? res.data : res.data.items || [];
      setQuestions(items);
      setQuestionCount(items.length);
    } catch (err) {
      console.error("Failed to fetch questions", err);
    }
  };
const fetchVersions = async () => {
  try {
    const res = await api.get("/mock/versions");  // adjust URL if needed
    setVersions(res.data || []);
  } catch (err) {
    console.error("Failed to fetch versions", err);
        setVersions([]); 
  }
};
  const PrepMaterialViewer = ({ material }) => {
  if (material.type === "pdf") {
    return (
      <PdfHighlighterComponent
        materialId={material.id}
        fileUrl={`http://localhost:5001/${material.filePath}`}
      />
    );
  }

  if (material.type === "txt" || material.type === "docx") {
    return (
      <TextHighlighter
        materialId={material.id}
        textContent={material.content}
      />
    );
  }

  return <div className="text-gray-400">Preview not supported</div>;
};

  const handleStartInterview = async () => {
    if (questionCount < 20) return alert("You need at least 20 questions to start.");
    setMockLoading(true);
    try {
      const res = await startMockInterview({ mode: "mixed" });
      const { interviewId, questions } = res.data;
      navigate(`/prep/mock/${interviewId}`, { state: { questions } });
    } catch (err) {
      console.error("Start failed", err);
      alert(err?.response?.data?.error || "Failed to start interview");
    } finally {
      setMockLoading(false);
    }
  };


    const handleStartVersion = async (versionId) => {
    try {
      const res = await api.post(`/mock/versions/${versionId}/start`);
      const { interviewId, questions } = res.data;

      if (!interviewId || !questions) {
        alert("No interview data returned from server.");
        return;
      }

      // ✅ Pass questions into location.state
      navigate(`/prep/mock/${interviewId}`, { state: { questions } });
    } catch (err) {
      console.error("Failed to start version", err);
      alert("Could not start version");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return alert("Please select a file");
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);
    formData.append("notes", notes);
    try {
      await uploadPrepMaterial(formData);
      setTitle("");
      setNotes("");
      setFile(null);
      setShowUploadModal(false);
      fetchMaterials();
    } catch (err) {
      alert("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    await deletePrepMaterial(id);
    fetchMaterials();
  };

  const handleEditSave = async () => {
    if (!editing.title) return alert("Title cannot be empty");
    setLoading(true);
    try {
      await updatePrepMaterial(editing.id, { title: editing.title, notes: editing.notes });
      setEditing(null);
      fetchMaterials();
    } catch (err) {
      alert("Update failed");
    } finally {
      setLoading(false);
    }
  };

  // Add single question
// Single add
const handleAddQuestion = async (e) => {
  e.preventDefault();
  if (!newQuestion.questionText.trim()) return setQuestionError("Question text required");
  try {
    const res = await api.post("/mock/questions", {
      question: newQuestion.questionText,
      domain: newQuestion.domain,
      versionId   
    });

    setQuestions(prev => [...prev, ...(Array.isArray(res.data) ? res.data : [res.data])]);

    // ✅ Safe fallback for NaN
    setQuestionCount((prev) => (prev ?? 0) + 1);

    setNewQuestion({ questionText: "", domain: "" });
    setQuestionError("");
    alert("1 question added successfully!"); // ✅ Explicitly single add message
  } catch (err) {
    console.error("Add question failed", err);
    setQuestionError(err?.response?.data?.error ?? "Failed to add question");
  }
};

// Bulk-add
const handleBulkAdd = async () => {
  if (!bulkText.trim()) return alert("Paste questions first");
 
  const lines = bulkText.split("\n").filter(Boolean);
  const questionsArray = lines.map(line => {
    const [question, domain] = line.split("|").map(s => s.trim());
    return { question, domain: domain || "", versionId };
  });
 
  try {
    // Send one by one
    const added = [];
    for (const q of questionsArray) {
      const res = await api.post("/mock/questions", q);
      added.push(res.data);
    }
 
    setQuestions(prev => [...prev, ...added]);
    setQuestionCount((prev) => (prev ?? 0) + added.length);
 
    setBulkText("");
    alert(`${added.length} question${added.length > 1 ? "s" : ""} added successfully!`);
  } catch (err) {
    console.error("Bulk add failed", err);
    alert(err?.response?.data?.error || "Failed to add questions");
  }
};
 

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
  };

  const isNewUser = questionCount === 0 || mode === "questions";

  return (
  <Layout>
    <div className="max-w-6xl mx-auto">  

    {/* ================= MOCK INTERVIEW OR NEW USER ================= */}
    {isNewUser ? (
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold mb-2">🎯 Welcome! Add your first 20 questions</h2>
        <p className="text-gray-600 mb-4">You need at least 20 questions to start a mock interview.</p>

        {questionError && <div className="text-red-600 mb-2">{questionError}</div>}


        {/* Single question form */}
        <form className="flex flex-col md:flex-row gap-2 mb-4" onSubmit={handleAddQuestion}>
          <input
            type="text"
            placeholder="Question text"
            className="flex-1 border p-2 rounded"
            value={newQuestion.questionText}
            onChange={(e) => setNewQuestion({ ...newQuestion, questionText: e.target.value })}
          />
          <input
            type="text"
            placeholder="Domain (optional)"
            className="border p-2 rounded"
            value={newQuestion.domain}
            onChange={(e) => setNewQuestion({ ...newQuestion, domain: e.target.value })}
          />
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Add
          </button>
        </form>

        {/* Bulk-add textarea */}
        <div className="mb-4">
          <p className="text-gray-600 mb-2">Or bulk-add questions (one per line, format: question | domain)</p>
          <textarea
            rows={6}
            className="w-full border p-2 rounded mb-2"
            placeholder="E.g., What is a closure in JS? | JavaScript"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
          <button
            onClick={handleBulkAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add Bulk Questions
          </button>
        </div>

        {/* List of added questions */}
        {questions.length > 0 && (
          <ul className="mt-4 list-disc list-inside text-gray-700">
            {questions.map((q, idx) => (
              <li key={q.id || idx}>
                {q.question}{" "}
                {q.domain && <span className="text-sm text-gray-500">({q.domain})</span>}
              </li>
            ))}
          </ul>
        )}

        <p className="mt-4 text-gray-500">Questions added: {questionCount}/20</p>
      </div>
    ) : (
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h1 className="text-2xl font-bold mb-2">🎤 Mock Interview</h1>
        <p className="text-gray-600 mb-4">
          Practice with <strong>20 random questions</strong> from your bank.
        </p>

        <div className="flex items-center gap-4 mb-4">
          <div className="text-3xl font-semibold">{questionCount}</div>
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-3 bg-indigo-600"
                style={{ width: `${Math.min(100, (questionCount / 20) * 100)}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {Math.min(questionCount, 20)} of 20 required
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            className={`px-4 py-2 rounded-lg text-white font-semibold ${
              questionCount >= 20
                ? "bg-indigo-600 hover:bg-indigo-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={handleStartInterview}
            disabled={questionCount < 20 || mockLoading}
          >
            {mockLoading ? "Starting..." : "Start Mock Interview"}
          </button>
        </div>
      </div>
    )}
 {versions.length > 0 && (
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-2">Practice by Version</h3>
        <ul className="space-y-2">
          {versions.map(v => (
            <li key={v.id}>
              <button
                className="text-blue-600 underline"
                onClick={() => handleStartVersion(v.id)}
              >
                Start Mock (Version {v.versionNumber})
              </button>
            </li>
          ))}
        </ul>
      </div>
    )}
    {/* ================= STUDY MATERIALS ================= */}
    <div className="flex justify-between items-center mb-4">
      <h1 className="text-2xl font-semibold">📚 Study Materials</h1>
      <button
        onClick={() => setShowUploadModal(true)}
        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        <Upload size={18} /> Upload
      </button>
    </div>

    {/* Storage Usage */}
    <div className="mb-4">
      <div className="flex justify-between text-sm mb-1">
        <span>Storage Usage</span>
        <span>
          {formatBytes(usage.usedBytes)} / {formatBytes(usage.limitBytes)}
        </span>
      </div>
      <div className="w-full h-3 bg-gray-300 rounded">
        <div
          className="h-3 bg-green-500 rounded"
          style={{ width: `${Math.min((usage.usedBytes / usage.limitBytes) * 100, 100)}%` }}
        />
      </div>
    </div>

    {/* Search + Filter */}
    <div className="flex flex-col md:flex-row gap-3 mb-4">
      <input
        type="text"
        placeholder="Search by title or notes"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="border p-2 rounded flex-1"
      />
      <select
        value={fileTypeFilter}
        onChange={(e) => setFileTypeFilter(e.target.value)}
        className="border p-2 rounded"
      >
        <option value="">All Types</option>
        <option value="pdf">PDF</option>
        <option value="docx">DOCX</option>
        <option value="txt">TXT</option>
        <option value="png">PNG</option>
        <option value="jpg">JPG</option>
      </select>
    </div>

    {/* ================= MATERIAL CARDS ================= */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredMaterials.map((m) => (
        <div
          key={m.id}
          className="bg-white shadow-md rounded-xl p-5 flex flex-col space-y-4"
        >
          {/* Header */}
          <div>
            <p className="font-semibold flex items-center gap-2 text-gray-800">
              <FileText size={18} /> {m.title}
            </p>
            {m.notes && <p className="text-sm text-gray-500 mt-1">{m.notes}</p>}
          </div>

          {/* Actions */}
          <div className="flex gap-4">

   <DownloadButton id={m.id} fileName={m.originalName} />



            <button
              onClick={() => setEditing({ ...m })}
              className="text-green-600 hover:text-green-800 text-sm flex items-center gap-1"
            >
              <Edit size={14} /> Edit
            </button>
            <button
              onClick={() => handleDelete(m.id)}
              className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1 ml-auto"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
         {/* View Details Button */}
<button
  onClick={() => navigate(`/prep/materials/${m.id}`)}
  className="mt-2 text-blue-600 underline text-sm"
>
  View Details
</button>
          
        </div>
      ))}
    </div>

    {/* ================= UPLOAD MODAL ================= */}
    {showUploadModal && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
        <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">Upload Study Material</h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <input
              type="text"
              placeholder="Title"
              className="w-full border p-2 rounded"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <textarea
              placeholder="Notes (optional)"
              className="w-full border p-2 rounded"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <input
              type="file"
              className="w-full"
              onChange={(e) => setFile(e.target.files[0])}
              required
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 border rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {loading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* ================= EDIT MODAL ================= */}
    {editing && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
        <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">Edit Material</h2>
          <input
            type="text"
            className="w-full border p-2 rounded mb-2"
            value={editing.title}
            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
          />
          <textarea
            className="w-full border p-2 rounded mb-2"
            value={editing.notes}
            onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setEditing(null)}
              className="px-4 py-2 border rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSave}
              disabled={loading}
              className="px-5 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    )}

  </div>
  </Layout>
);
}